import type { SnapdomPlugin } from '@zumer/snapdom'

/**
 * snapdom plugin that makes the re-laid-out clone match the screen (sticky and
 * fixed boxes, single-line text, scrolled grid/flex containers, `all: unset`
 * buttons). It measures the live page synchronously, undoing any probe before
 * returning, and edits only the clone.
 *
 * JS source, not TS: it also runs inside the sandboxed HTML-interface iframes.
 * Evaluating it defines `window.pyplanSnapshotFidelity`, a plugin factory.
 */
export const SNAPSHOT_FIDELITY_JS = `
window.pyplanSnapshotFidelity = function () {
  var state = null;

  function scrollOffsets(doc) {
    var list = [];
    var all = doc.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].scrollTop || all[i].scrollLeft) list.push([all[i], all[i].scrollTop, all[i].scrollLeft]);
    }
    return list;
  }

  function restoreScroll(list, win, winScroll) {
    list.forEach(function (s) { s[0].scrollTop = s[1]; s[0].scrollLeft = s[2]; });
    win.scrollTo(winScroll[0], winScroll[1]);
  }

  // Applies styles, measures, and puts the inline style back before returning.
  function measureWith(el, styles, measure) {
    var saved = el.getAttribute('style');
    Object.keys(styles).forEach(function (k) { el.style.setProperty(k, styles[k], 'important'); });
    try { return measure(); } finally {
      if (saved === null) el.removeAttribute('style'); else el.setAttribute('style', saved);
    }
  }

  function measure(root) {
    var doc = root.ownerDocument, win = doc.defaultView;
    var scrolls = scrollOffsets(doc), winScroll = [win.scrollX, win.scrollY];
    var edits = new Map();
    function edit(el, prop, value) {
      if (!edits.has(el)) edits.set(el, {});
      edits.get(el)[prop] = value;
    }

    var range = doc.createRange(), seen = new Set();
    var walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      var el = n.parentElement;
      if (!el || seen.has(el) || !n.textContent.trim()) continue;
      seen.add(el);
      if (win.getComputedStyle(el).whiteSpace !== 'normal') continue;
      range.selectNodeContents(el);
      var rects = range.getClientRects(), top = null, single = true;
      for (var i = 0; i < rects.length; i++) {
        if (rects[i].width === 0) continue;
        if (top === null) top = rects[i].top;
        else if (Math.abs(rects[i].top - top) > 2) { single = false; break; }
      }
      if (single && top !== null) edit(el, 'white-space', 'nowrap');
    }

    var all = root.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var node = all[j], cs = win.getComputedStyle(node);
      var own = cs.transform && cs.transform !== 'none' ? ' ' + cs.transform : '';
      if (cs.position === 'sticky') {
        var stuck = node.getBoundingClientRect();
        var flow = measureWith(node, { position: 'relative', top: 'auto', bottom: 'auto', left: 'auto', right: 'auto' },
          function () { return node.getBoundingClientRect(); });
        edit(node, 'position', 'relative');
        ['top', 'bottom', 'left', 'right'].forEach(function (p) { edit(node, p, 'auto'); });
        edit(node, 'transform', 'translate(' + (stuck.left - flow.left) + 'px,' + (stuck.top - flow.top) + 'px)' + own);
        if (cs.zIndex === 'auto') edit(node, 'z-index', '1');
      } else if (cs.position === 'fixed') {
        var r0 = node.getBoundingClientRect();
        if (!(r0.width > 0 && r0.height > 0)) continue;
        // Changing position resets the scroll of scroll containers, so it is restored before measuring.
        var r1 = measureWith(node, { position: 'absolute', top: '0px', left: '0px', right: 'auto', bottom: 'auto', width: r0.width + 'px', height: r0.height + 'px' },
          function () { restoreScroll(scrolls, win, winScroll); return node.getBoundingClientRect(); });
        edit(node, 'position', 'absolute');
        edit(node, 'top', (r0.top - r1.top) + 'px');
        edit(node, 'left', (r0.left - r1.left) + 'px');
        edit(node, 'right', 'auto');
        edit(node, 'bottom', 'auto');
        edit(node, 'width', r0.width + 'px');
        edit(node, 'height', r0.height + 'px');
      }
    }
    restoreScroll(scrolls, win, winScroll);

    // Scrolled grid/flex containers carry their offset on the children while the
    // clone is taken (identical on screen); undone right after cloning.
    var moved = [];
    scrolls.forEach(function (s) {
      var box = s[0];
      if (!/(grid|flex)/.test(win.getComputedStyle(box).display)) return;
      Array.prototype.forEach.call(box.children, function (child) {
        var ccs = win.getComputedStyle(child);
        if (ccs.position === 'fixed') return;
        var shift = 'translate(' + (-s[2]) + 'px,' + (-s[1]) + 'px)';
        var planned = edits.has(child) && edits.get(child).transform;
        var current = ccs.transform && ccs.transform !== 'none' ? ' ' + ccs.transform : '';
        moved.push([child, child.getAttribute('style')]);
        child.style.setProperty('transform', shift + (planned ? ' ' + planned : current), 'important');
        if (planned) edit(child, 'transform', shift + ' ' + planned);
      });
      box.scrollTop = 0;
      box.scrollLeft = 0;
    });
    return { edits: edits, moved: moved, scrolls: scrolls, win: win, winScroll: winScroll };
  }

  return {
    name: 'pyplan-fidelity',
    beforeClone: function (ctx) {
      state = measure(ctx.element);
    },
    afterClone: function (ctx) {
      if (!state) return;
      var s = state;
      state = null;
      s.moved.forEach(function (m) {
        if (m[1] === null) m[0].removeAttribute('style'); else m[0].setAttribute('style', m[1]);
      });
      restoreScroll(s.scrolls, s.win, s.winScroll);
      ctx.nodeMap.forEach(function (live, clone) {
        var props = s.edits.get(live);
        if (!props || !clone.style) return;
        Object.keys(props).forEach(function (k) { clone.style.setProperty(k, props[k], 'important'); });
      });
      Array.prototype.forEach.call(ctx.clone.querySelectorAll('button'), function (btn) {
        var live = ctx.nodeMap.get(btn);
        if (!live || s.win.getComputedStyle(live).display !== 'inline') return;
        var span = btn.ownerDocument.createElement('span');
        Array.prototype.forEach.call(btn.attributes, function (a) { span.setAttribute(a.name, a.value); });
        while (btn.firstChild) span.appendChild(btn.firstChild);
        btn.replaceWith(span);
      });
    }
  };
};
`

declare global {
  interface Window {
    pyplanSnapshotFidelity?: () => SnapdomPlugin
  }
}

/** A fresh fidelity plugin for a snapdom capture of the app's own document. */
export const getSnapshotFidelityPlugin = (): SnapdomPlugin => {
  if (!window.pyplanSnapshotFidelity) {
    const script = document.createElement('script')
    script.textContent = SNAPSHOT_FIDELITY_JS
    document.head.appendChild(script)
    script.remove()
  }
  return window.pyplanSnapshotFidelity!()
}
